import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { suppliers as suppliersApi } from "@/api";
import { formatApiErrorDetail } from "@/api";
import { useCategories } from "@/hooks/useCategories";
import { ArrowLeft, Save } from "lucide-react";

export default function SupplierForm() {
  const { id } = useParams();
  const CATEGORIES = useCategories();
  const isEdit = Boolean(id);
  const nav = useNavigate();

  const [form, setForm] = useState({
    name: "",
    rfc: "",
    sap_id: "",
    category: "Materia prima cárnica",
    contact_email: "",
    logo_url: "",
    description: "",
  });
  const [originalCategory, setOriginalCategory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (isEdit) {
      suppliersApi.get(id).then((s) => {
        setOriginalCategory(s.category || "Materia prima cárnica");
        setForm({
          name: s.name || "",
          rfc: s.rfc || "",
          sap_id: s.sap_id || "",
          category: s.category || "Materia prima cárnica",
          contact_email: s.contact_email || "",
          logo_url: s.logo_url || "",
          description: s.description || "",
        });
      });
    }
  }, [id, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      const saved = isEdit ? await suppliersApi.update(id, form) : await suppliersApi.create(form);
      setMsg(isEdit ? "Proveedor actualizado." : "Proveedor creado.");
      setTimeout(() => nav(`/proveedores/${saved.id}`), 600);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const F = (key, label, props = {}) => (
    <div>
      <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">{label}</label>
      <input
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="input-field mt-1"
        data-testid={`supplier-form-${key}`}
        {...props}
      />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link to={isEdit ? `/proveedores/${id}` : "/proveedores"} className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-4" data-testid="back-link">
        <ArrowLeft size={14} /> Volver
      </Link>
      <div className="mb-6">
        <div className="font-hand text-xl text-[var(--lala-blue)]">{isEdit ? "editar proveedor" : "nuevo proveedor"}</div>
        <h1 className="font-display text-4xl">{isEdit ? form.name || "Editar" : "Alta de proveedor"}</h1>
      </div>

      <form onSubmit={submit} className="sketch-border bg-[var(--cream-soft)] p-6 grid gap-4" data-testid="supplier-form">
        <div className="grid md:grid-cols-2 gap-4">
          {F("name", "Razón social *", { required: true, placeholder: "Lácteos del Bajío" })}
          {F("rfc", "RFC (opcional)", { placeholder: "LAB920101AB1", maxLength: 13 })}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {F("sap_id", "SAP Supplier ID (opcional)", { placeholder: "MP-100245" })}
          <div>
            <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Categoría *</label>
            <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-field mt-1" data-testid="supplier-form-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {isEdit && originalCategory && form.category !== originalCategory && (
              <p className="mt-2 text-xs text-[var(--ink-soft)] border-l-2 border-[#FFC400] pl-2" data-testid="supplier-form-category-warning">
                Al cambiar la categoría, Costo y Continuidad usarán los KPIs de la nueva categoría o, si no tiene
                propios, la rúbrica &quot;Por defecto&quot;. Las capturas anteriores de estos pilares se conservan, pero
                no se muestran en la matriz salvo que la nueva categoría se agregue a esos KPIs en el Catálogo de KPIs.
              </p>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {F("contact_email", "Email de contacto", { type: "email", placeholder: "contacto@empresa.com" })}
          {F("logo_url", "URL de logo (opcional)", { placeholder: "https://…" })}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Descripción</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="input-field mt-1" rows={3}
            placeholder="Breve descripción del proveedor"
            data-testid="supplier-form-description"
          />
        </div>

        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="supplier-form-error">{err}</div>}
        {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="supplier-form-success">{msg}</div>}

        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="supplier-form-submit">
            <Save size={16} /> {busy ? "Guardando…" : (isEdit ? "Guardar cambios" : "Crear proveedor")}
          </button>
        </div>
      </form>
    </div>
  );
}
